import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ArrowLeft, Plus, BookOpen, Clock, CheckCircle, Moon, Sun, LogOut, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

const ActividadesDocente = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('todos');

  useEffect(() => {
    fetchActividades();
  }, []);

  const fetchActividades = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/actividades`,
        { withCredentials: true }
      );
      setActividades(data);
    } catch (error) {
      console.error('Error fetching actividades:', error);
      toast.error('Error al cargar actividades');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getEstadoBadgeColor = (estado) => {
    const colors = {
      borrador: 'bg-gray-500',
      privado: 'bg-blue-500',
      revision: 'bg-yellow-500',
      aprobado: 'bg-green-500',
      rechazado: 'bg-red-500'
    };
    return colors[estado] || 'bg-gray-500';
  };

  const filteredActividades = actividades.filter(act => {
    const matchSearch = act.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       act.descripcion.toLowerCase().includes(searchTerm.toLowerCase());
    const matchFilter = filterEstado === 'todos' || act.estado === filterEstado;
    return matchSearch && matchFilter;
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/docente')}
                data-testid="back-button"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Mis Actividades</h1>
                <p className="text-sm text-muted-foreground">Gestionar actividades didácticas</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle-button"
              >
                {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Actions Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Buscar actividades..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2">
            <select
              className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
              data-testid="filter-estado"
            >
              <option value="todos">Todos los estados</option>
              <option value="borrador">Borrador</option>
              <option value="privado">Privado</option>
              <option value="revision">En revisión</option>
              <option value="aprobado">Aprobado</option>
              <option value="rechazado">Rechazado</option>
            </select>
            <Button 
              onClick={() => navigate('/docente/actividades/nueva')}
              data-testid="btn-crear-actividad"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nueva Actividad
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardDescription className="text-xs uppercase tracking-wider">Total</CardDescription>
              <CardTitle className="text-2xl font-bold">{actividades.length}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardDescription className="text-xs uppercase tracking-wider">Borradores</CardDescription>
              <CardTitle className="text-2xl font-bold">{actividades.filter(a => a.estado === 'borrador').length}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardDescription className="text-xs uppercase tracking-wider">En Revisión</CardDescription>
              <CardTitle className="text-2xl font-bold">{actividades.filter(a => a.estado === 'revision').length}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardDescription className="text-xs uppercase tracking-wider">Aprobadas</CardDescription>
              <CardTitle className="text-2xl font-bold">{actividades.filter(a => a.estado === 'aprobado').length}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Activities List */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Cargando actividades...</p>
          </div>
        ) : filteredActividades.length === 0 ? (
          <Card className="border-2 border-dashed border-border">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <BookOpen className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">No hay actividades</p>
              <p className="text-muted-foreground mb-4">Comienza creando tu primera actividad didáctica</p>
              <Button onClick={() => navigate('/docente/actividades/nueva')}>
                <Plus className="h-4 w-4 mr-2" />
                Crear Primera Actividad
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4" data-testid="actividades-list">
            {filteredActividades.map((actividad, index) => (
              <Card 
                key={actividad.actividad_id}
                className="hover:shadow-lg transition-all cursor-pointer border-border"
                onClick={() => navigate(`/docente/actividades/${actividad.actividad_id}`)}
                data-testid={`actividad-card-${index}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1" onClick={() => navigate(`/docente/actividades/${actividad.actividad_id}`)}>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getEstadoBadgeColor(actividad.estado)}>
                          {actividad.estado.charAt(0).toUpperCase() + actividad.estado.slice(1)}
                        </Badge>
                        <Badge variant="outline">{actividad.tipo_actividad}</Badge>
                        {actividad.privada && <Badge variant="secondary">Privada</Badge>}
                      </div>
                      <CardTitle className="text-xl mb-2">{actividad.titulo}</CardTitle>
                      <CardDescription className="line-clamp-2">{actividad.descripcion}</CardDescription>
                    </div>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/docente/actividades/${actividad.actividad_id}/asignar`);
                      }}
                      size="sm"
                      data-testid={`btn-asignar-${index}`}
                    >
                      Asignar
                    </Button>
                  </div>
                  <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>Creada: {new Date(actividad.created_at).toLocaleDateString()}</span>
                    </div>
                    {actividad.permite_nota && (
                      <div className="flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        <span>Calificable</span>
                      </div>
                    )}
                    {actividad.permite_logro && (
                      <div className="flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        <span>Mide logro</span>
                      </div>
                    )}
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ActividadesDocente;
